import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
} from '@mui/material';
import { format } from 'date-fns';
import { getVolunteer, getVolunteerRegistrations, updateVolunteer } from '../services/api';
import { Volunteer, Registration } from '../models/types';

const VolunteerDetailAdminPage: React.FC = () => {
  const { volunteerId } = useParams<{ volunteerId: string }>();
  const navigate = useNavigate();
  
  const [volunteer, setVolunteer] = useState<Volunteer | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);  const [error, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    commodity: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!volunteerId) return;
      
      try {
        setLoading(true);
        
        const volunteerData = await getVolunteer(volunteerId);
        setVolunteer(volunteerData);
        
        const registrationsData = await getVolunteerRegistrations(volunteerId);
        setRegistrations(registrationsData);
        
      } catch (err) {
        console.error('Error fetching volunteer details:', err);
        setError('Failed to load volunteer details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [volunteerId]); 

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>              Lade Helfer-Details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (!volunteer) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Helfer nicht gefunden</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }  const handleOpenEditDialog = () => {
    setEditForm({
      name: volunteer?.name || '',
      email: volunteer?.email || '',
      phoneNumber: volunteer?.phoneNumber || '',
      commodity: volunteer?.commodity || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleCommodityChange = (event: SelectChangeEvent) => {
    setEditForm({ ...editForm, commodity: event.target.value });
  };
  const handleSaveVolunteer = async () => {
    if (!volunteer || !volunteerId) return;
    
    try {
      setIsSaving(true);
      await updateVolunteer(volunteerId, {
        name: editForm.name,
        email: editForm.email || undefined,
        phoneNumber: editForm.phoneNumber || undefined,
        commodity: editForm.commodity || undefined,
      });
      
      // Refresh volunteer data
      const updatedVolunteer = await getVolunteer(volunteerId);
      setVolunteer(updatedVolunteer);
      
      setIsEditDialogOpen(false);
      setSuccessMessage('Helfer wurde erfolgreich aktualisiert');
    } catch (err: any) {
      console.error('Error updating volunteer:', err);
      
      // Check for specific error about email already in use
      if (err.response && err.response.status === 409) {
        setError('Diese E-Mail-Adresse wird bereits von einem anderen Helfer verwendet.');
      } else {
        setError('Fehler beim Aktualisieren des Helfers.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin')}
              sx={{ mb: 1 }}
            >              Zurück zur Helferliste
            </Button>
            <Typography variant="h4">
              {volunteer.name}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenEditDialog}
          >
            Helfer bearbeiten
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Helfer-Informationen
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>E-Mail:</strong> {volunteer.email}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Telefon:</strong> {volunteer.phoneNumber || 'Nicht angegeben'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">                <strong>Organisation:</strong> {volunteer.commodity || 'Nicht angegeben'}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Gesamte Arbeitszeit:</strong> {volunteer.totalMinutesWorked || 0} Minuten
                </Typography>
              </Box>
            </Box>
            
           
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Anmeldungen
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {registrations.length === 0 ? (
            <Alert severity="info">
              Dieser Helfer hat sich noch für keine Jobs angemeldet.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Zeitfenster</TableCell>
                    <TableCell>Anmeldedatum</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gearbeitete Minuten</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrations.map((registration) => (
                    <TableRow key={registration.id}>
                      <TableCell>{registration.jobName}</TableCell>
                      <TableCell>
                        {registration.timeslotStart ? 
                          format(new Date(registration.timeslotStart), 'MMM d, h:mm a') : 
                          'Unknown'}
                      </TableCell>
                      <TableCell>
                        {format(new Date(registration.registeredAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>                        {registration.hasAttended ? (
                          <Chip label="Anwesend" color="success" size="small" />
                        ) : registration.isConfirmed ? (
                          <Chip label="Bestätigt" color="primary" size="small" />
                        ) : (
                          <Chip label="Ausstehend" color="warning" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{registration.actualMinutesWorked || 'Nicht erfasst'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => navigate(`/admin/registrations/edit/${registration.id}`)}
                        >
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}        </Paper>
      </Box>

      {/* Edit Volunteer Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>Helfer bearbeiten</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Bearbeiten Sie die Informationen des Helfers:
          </DialogContentText>
            <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editForm.name}
            onChange={handleFormChange}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="email"
            label="E-Mail"
            type="email"
            fullWidth
            variant="outlined"
            value={editForm.email}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="phoneNumber"
            label="Telefon"
            type="text"
            fullWidth
            variant="outlined"
            value={editForm.phoneNumber}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="commodity-select-label">Organisation</InputLabel>
            <Select
              labelId="commodity-select-label"
              id="commodity-select"
              value={editForm.commodity}
              label="Organisation"
              onChange={handleCommodityChange}
            >
              <MenuItem value="">Keine Organisation</MenuItem>
              <MenuItem value="Musikverein">Musikverein</MenuItem>
              <MenuItem value="Kameradschaftsbund">Kameradschaftsbund</MenuItem>
              <MenuItem value="Feuerwehr">Feuerwehr</MenuItem>
              <MenuItem value="Landjugend">Landjugend</MenuItem>
              <MenuItem value="Sonstige">Sonstige</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isSaving}>Abbrechen</Button>
          <Button 
            onClick={handleSaveVolunteer}
            variant="contained"
            disabled={isSaving || !editForm.name}
          >
            {isSaving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  );
};

export default VolunteerDetailAdminPage;

